import {
  forwardRef,
  useEffect,
  useId,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/Button'
import { t } from '@/i18n'

/* ------------------------------------------------------------------ Select */

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  /** Classes for the field as a whole — margins and layout. See Input.wrapperClassName. */
  wrapperClassName?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, wrapperClassName, label, error, id, children, ...props }, ref) => {
    const generatedId = useId()
    const selectId = id ?? generatedId

    return (
      <div className={cn('flex flex-col gap-1.5', wrapperClassName)}>
        {label && (
          <label htmlFor={selectId} className="text-sm font-medium">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'surface-raised h-11 w-full rounded-lg border px-3 text-base outline-none',
            'focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/25',
            error && 'border-danger-500',
            className,
          )}
          {...props}
        >
          {children}
        </select>
        {error && <p className="text-sm text-danger-500">{error}</p>}
      </div>
    )
  },
)
Select.displayName = 'Select'

/* ------------------------------------------------------------------- Badge */

type BadgeTone = 'neutral' | 'success' | 'warn' | 'danger' | 'accent'

const badgeTones: Record<BadgeTone, string> = {
  neutral: 'surface-sunken text-[var(--text-secondary)]',
  success: 'bg-success-500/15 text-success-500',
  warn: 'bg-warn-500/15 text-warn-500',
  danger: 'bg-danger-500/15 text-danger-500',
  accent: 'bg-[var(--accent)]/15 text-[var(--accent)]',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: BadgeTone
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        badgeTones[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/* ------------------------------------------------------------------- Modal */

/**
 * A centred dialog on desktop that becomes a bottom sheet on a phone, because a full-height
 * centred modal is unusable one-handed at a counter.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  wide,
  dismissible = true,
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
  /**
   * False for a dialog the user has to finish — a forced password change.
   *
   * It removes the ways out rather than ignoring them. A close button that does nothing when
   * pressed reads as a broken screen, and somebody presses it three times before believing it.
   */
  dismissible?: boolean
}) {
  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && dismissible) onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    // Stops the page behind scrolling while the sheet is up.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
    }
  }, [open, onClose, dismissible])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      {dismissible ? (
        <button
          type="button"
          aria-label={t('Close')}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        />
      ) : (
        <div className="absolute inset-0 bg-black/50" />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'surface-raised relative flex max-h-[92dvh] w-full flex-col rounded-t-2xl border shadow-xl',
          'sm:rounded-2xl',
          wide ? 'sm:max-w-3xl' : 'sm:max-w-lg',
        )}
      >
        <header className="flex items-start gap-3 border-b border-[var(--border-subtle)] px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold">{title}</h2>
            {description && (
              <p className="mt-0.5 text-sm text-[var(--text-secondary)]">{description}</p>
            )}
          </div>
          {dismissible && (
            <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('Close')}>
              <X className="h-5 w-5" />
            </Button>
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && (
          <footer className="safe-bottom flex justify-end gap-2 border-t border-[var(--border-subtle)] px-5 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- EmptyState */

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="surface-sunken flex h-14 w-14 items-center justify-center rounded-2xl">
        <Icon className="h-6 w-6 text-[var(--text-secondary)]" />
      </div>
      <h3 className="mt-4 font-medium">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-[var(--text-secondary)]">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

/* -------------------------------------------------------------------- Tabs */

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: readonly { value: T; label: string; count?: number }[]
  value: T
  onChange: (value: T) => void
}) {
  return (
    <div role="tablist" className="surface-sunken flex gap-1 overflow-x-auto rounded-xl p-1">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={value === tab.value}
          onClick={() => onChange(tab.value)}
          className={cn(
            'flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            value === tab.value
              ? 'surface-raised shadow-sm'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          )}
        >
          {tab.label}
          {tab.count !== undefined && tab.count > 0 && (
            <span className="tabular rounded-full bg-[var(--accent)]/15 px-1.5 text-xs text-[var(--accent)]">
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ Toggle */

export function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  hint?: string
}) {
  const id = useId()
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 py-1">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-[var(--border-subtle)] accent-[var(--accent)]"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-[var(--text-secondary)]">{hint}</span>}
      </span>
    </label>
  )
}
